import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import type { DocumentMetadataOption } from '@/types/workflow';
import { metadataApi, type MetadataOption } from '@/services/api';

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (data: { file: File; metadata: { proForma: string; documentType: string; territory: string } }) => void;
}

// Map backend response (id/name) to frontend format (value/label)
const mapToOption = (option: MetadataOption): DocumentMetadataOption => ({
  value: option.id,
  label: option.name
});

export default function DocumentUploadModal({ open, onClose, onUpload }: DocumentUploadModalProps) {
  const [validationError, setValidationError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  
  const [proFormaOptions, setProFormaOptions] = useState<DocumentMetadataOption[]>([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<DocumentMetadataOption[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<DocumentMetadataOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedProForma, setSelectedProForma] = useState<DocumentMetadataOption | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentMetadataOption | null>(null);
  const [selectedTerritory, setSelectedTerritory] = useState<DocumentMetadataOption | null>(null);

  useEffect(() => {
    if (open) {
      const fetchOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const [proFormaData, documentTypeData, territoryData] = await Promise.all([
            metadataApi.getProFormaOptions(),
            metadataApi.getDocumentTypeOptions(),
            metadataApi.getTerritoryOptions()
          ]);
          
          setProFormaOptions(proFormaData.map(mapToOption));
          setDocumentTypeOptions(documentTypeData.map(mapToOption));
          setTerritoryOptions(territoryData.map(mapToOption));
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch metadata options');
          console.error('Error fetching metadata options:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();
    }
  }, [open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = () => {
    setValidationError('');

    if (!file) {
      setValidationError('Please select a file to upload');
      return;
    }

    if (!selectedProForma || !selectedDocumentType || !selectedTerritory) {
      setValidationError('All metadata fields are required');
      return;
    }

    const uploadData = {
      file,
      metadata: {
        proForma: selectedProForma.value,
        documentType: selectedDocumentType.value,
        territory: selectedTerritory.value
      }
    };

    onUpload(uploadData);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setSelectedProForma(null);
    setSelectedDocumentType(null);
    setSelectedTerritory(null);
    setValidationError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Upload New Document</Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {!loading && !error && (
            <>
              {/* File Upload Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  File Upload
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: file ? 'primary.main' : 'grey.300',
                    bgcolor: file ? 'primary.50' : 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50'
                    }
                  }}
                  onClick={() => document.getElementById('file-upload-input')?.click()}
                >
                  <input
                    id="file-upload-input"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CloudUploadIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: file ? 'primary.main' : 'grey.400',
                      mb: 2 
                    }} 
                  />
                  {file ? (
                    <Box>
                      <Typography variant="body1" color="primary.main" fontWeight="medium">
                        {file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                        Click to change file
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        Drop your file here or click to browse
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports PDF, DOC, DOCX, TXT files
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Document Metadata Section */}
              <Typography variant="subtitle1" gutterBottom>
                Document Metadata
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This metadata will be used to match the document to the appropriate workflow.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={proFormaOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedProForma}
                    onChange={(_, newValue) => setSelectedProForma(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pro Forma *"
                        variant="outlined"
                        placeholder="Select pro forma"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Autocomplete
                    options={documentTypeOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedDocumentType}
                    onChange={(_, newValue) => setSelectedDocumentType(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Document Type *"
                        variant="outlined"
                        placeholder="Select document type"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Autocomplete
                    options={territoryOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedTerritory}
                    onChange={(_, newValue) => setSelectedTerritory(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Territory *"
                        variant="outlined"
                        placeholder="Select territory"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </FormControl>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={loading || !!error || !file || !selectedProForma || !selectedDocumentType || !selectedTerritory}
          sx={{ minWidth: 120 }}
        >
          Upload Document
        </Button>
      </DialogActions>
    </Dialog>
  );
}